import React, { useState, useEffect } from 'react';
import { X, Calculator, Sun, Building2, ArrowRight, Zap, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';

interface SimulationModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialRole?: 'consumer' | 'producer';
}

const CONSUMER_PROFILES = [
  { value: 'supermarche', labelKey: 'simulator.profiles.supermarche', avgMin: 200000, avgMax: 800000 },
  { value: 'bureau', labelKey: 'simulator.profiles.bureau', avgMin: 20000, avgMax: 100000 },
  { value: 'pme', labelKey: 'simulator.profiles.pme', avgMin: 30000, avgMax: 100000 },
  { value: 'resident', labelKey: 'simulator.profiles.resident', avgMin: 2500, avgMax: 5000 },
  { value: 'restaurant', labelKey: 'simulator.profiles.restaurant', avgMin: 15000, avgMax: 80000 },
];

export function SimulationModal({ isOpen, onClose, initialRole = 'consumer' }: SimulationModalProps) {
  const { t } = useTranslation();
  const [role, setRole] = useState<'consumer' | 'producer'>(initialRole);
  const [consumerProfile, setConsumerProfile] = useState('');
  const [annualConsumption, setAnnualConsumption] = useState('');
  const [currentRate, setCurrentRate] = useState('');
  const [installedPower, setInstalledPower] = useState('');
  const [selfConsumptionRate, setSelfConsumptionRate] = useState('30');
  const [showResults, setShowResults] = useState(false);
  const [reductionRate, setReductionRate] = useState(0.15);

  useEffect(() => {
    setRole(initialRole);
    setShowResults(false);
    setConsumerProfile('');
    setAnnualConsumption('');
    setCurrentRate('');
    setInstalledPower('');
    setSelfConsumptionRate('30');
  }, [isOpen, initialRole]);

  const handleProfileChange = (value: string) => {
    setConsumerProfile(value);
    setShowResults(false);
    const profile = CONSUMER_PROFILES.find(p => p.value === value);
    if (profile) {
      const avg = Math.round((profile.avgMin + profile.avgMax) / 2);
      setAnnualConsumption(String(avg));
    }
  };

  useEffect(() => {
    supabase
      .from('billing_config')
      .select('value')
      .eq('key', 'simulateur_reduction')
      .maybeSingle()
      .then(({ data }) => {
        if (data) setReductionRate(Number(data.value));
      });
  }, []);

  const calculateConsumerSavings = () => {
    const consumption = parseFloat(annualConsumption);
    const rate = parseFloat(currentRate);
    if (!consumption || !rate) return null;
    const communityRate = rate * (1 - reductionRate);
    const annualSavings = consumption * (rate - communityRate);
    return {
      currentCost: (consumption * rate).toFixed(0),
      newCost: (consumption * communityRate).toFixed(0),
      savings: annualSavings.toFixed(0),
      monthlySavings: (annualSavings / 12).toFixed(0),
      percentageSaved: (reductionRate * 100).toFixed(0),
    };
  };

  const calculateProducerRevenue = () => {
    const power = parseFloat(installedPower);
    const selfRate = parseFloat(selfConsumptionRate) / 100;
    if (!power) return null;
    const annualProduction = power * 950;
    const surplus = annualProduction * (1 - selfRate);
    const classicInjectionPrice = 0.02;
    const sunIsUpPrice = 0.065;
    const classicRevenue = surplus * classicInjectionPrice;
    const sunIsUpRevenue = surplus * sunIsUpPrice;
    const extraRevenue = sunIsUpRevenue - classicRevenue;
    return {
      annualProduction: Math.round(annualProduction),
      surplus: Math.round(surplus),
      classicRevenue: classicRevenue.toFixed(0),
      sunIsUpRevenue: sunIsUpRevenue.toFixed(0),
      extraRevenue: extraRevenue.toFixed(0),
      extraRevenuePercent: ((sunIsUpPrice / classicInjectionPrice - 1) * 100).toFixed(0),
    };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowResults(true);
  };

  const consumerResults = role === 'consumer' ? calculateConsumerSavings() : null;
  const producerResults = role === 'producer' ? calculateProducerRevenue() : null;

  const selectedProfile = CONSUMER_PROFILES.find(p => p.value === consumerProfile);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white rounded-t-3xl border-b px-6 py-4 flex items-center justify-between z-10" style={{ borderColor: '#e5e5e5' }}>
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5" style={{ color: '#F8BD3D' }} />
            <h2 className="text-xl font-bold" style={{ color: '#1a1a1a' }}>{t('simulator.title')}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full transition-colors hover:bg-gray-100"
          >
            <X className="w-5 h-5" style={{ color: '#666' }} />
          </button>
        </div>

        <div className="p-6">
          <div className="flex justify-center mb-6">
            <div className="inline-flex rounded-full p-1" style={{ backgroundColor: '#f5f5f5' }}>
              <button
                onClick={() => { setRole('consumer'); setShowResults(false); }}
                className="px-5 py-2.5 rounded-full font-semibold transition-all duration-300 flex items-center gap-2 text-sm"
                style={{
                  backgroundColor: role === 'consumer' ? '#F8BD3D' : 'transparent',
                  color: role === 'consumer' ? '#1a1a1a' : '#666666'
                }}
              >
                <Building2 className="w-4 h-4" />
                {t('simulator.tabs.consumer')}
              </button>
              <button
                onClick={() => { setRole('producer'); setShowResults(false); }}
                className="px-5 py-2.5 rounded-full font-semibold transition-all duration-300 flex items-center gap-2 text-sm"
                style={{
                  backgroundColor: role === 'producer' ? '#F8BD3D' : 'transparent',
                  color: role === 'producer' ? '#1a1a1a' : '#666666'
                }}
              >
                <Sun className="w-4 h-4" />
                {t('simulator.tabs.producer')}
              </button>
            </div>
          </div>

          {role === 'consumer' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="p-4 rounded-2xl" style={{ backgroundColor: '#FFF5E1' }}>
                <p className="text-sm" style={{ color: '#666' }}>
                  {t('simulator.consumer.intro')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1a1a1a' }}>
                  {t('simulator.consumer.profileLabel')}
                </label>
                <select
                  value={consumerProfile}
                  onChange={(e) => handleProfileChange(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border transition-colors outline-none focus:ring-2 appearance-none bg-no-repeat"
                  style={{ borderColor: '#e5e5e5', backgroundColor: '#fafafa', backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundPosition: 'right 12px center' }}
                >
                  <option value="">{t('simulator.consumer.profilePlaceholder')}</option>
                  {CONSUMER_PROFILES.map(p => (
                    <option key={p.value} value={p.value}>{t(p.labelKey)}</option>
                  ))}
                </select>
                {selectedProfile && (
                  <p className="text-xs mt-1" style={{ color: '#999' }}>
                    {t('simulator.consumer.typicalRange', {
                      min: selectedProfile.avgMin.toLocaleString('fr'),
                      max: selectedProfile.avgMax.toLocaleString('fr')
                    })}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1a1a1a' }}>
                  {t('simulator.consumer.consumptionLabel')}
                </label>
                <input
                  type="number"
                  value={annualConsumption}
                  onChange={(e) => { setAnnualConsumption(e.target.value); setShowResults(false); }}
                  className="w-full px-4 py-3 rounded-xl border transition-colors outline-none focus:ring-2"
                  style={{ borderColor: '#e5e5e5', backgroundColor: '#fafafa' }}
                  placeholder={t('simulator.consumer.consumptionPlaceholder')}
                  required
                  min="1"
                />
                {!selectedProfile && (
                  <p className="text-xs mt-1" style={{ color: '#999' }}>{t('simulator.consumer.consumptionHint')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1a1a1a' }}>
                  {t('simulator.consumer.rateLabel')}
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={currentRate}
                  onChange={(e) => { setCurrentRate(e.target.value); setShowResults(false); }}
                  className="w-full px-4 py-3 rounded-xl border transition-colors outline-none focus:ring-2"
                  style={{ borderColor: '#e5e5e5', backgroundColor: '#fafafa' }}
                  placeholder={t('simulator.consumer.ratePlaceholder')}
                  required
                  min="0.01"
                />
                <p className="text-xs mt-1" style={{ color: '#999' }}>{t('simulator.consumer.rateHint')}</p>
              </div>

              <button
                type="submit"
                className="w-full py-4 rounded-full font-bold text-base transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg hover:-translate-y-0.5"
                style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
              >
                <Calculator className="w-5 h-5" />
                {t('simulator.consumer.submitButton')}
              </button>
            </form>
          )}

          {role === 'producer' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="p-4 rounded-2xl" style={{ backgroundColor: '#FFF5E1' }}>
                <p className="text-sm" style={{ color: '#666' }}>
                  {t('simulator.producer.intro')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1a1a1a' }}>
                  {t('simulator.producer.powerLabel')}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={installedPower}
                  onChange={(e) => { setInstalledPower(e.target.value); setShowResults(false); }}
                  className="w-full px-4 py-3 rounded-xl border transition-colors outline-none focus:ring-2"
                  style={{ borderColor: '#e5e5e5', backgroundColor: '#fafafa' }}
                  placeholder={t('simulator.producer.powerPlaceholder')}
                  required
                  min="1"
                />
                <p className="text-xs mt-1" style={{ color: '#999' }}>{t('simulator.producer.powerHint')}</p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1a1a1a' }}>
                  {t('simulator.producer.selfConsumptionLabel')}
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="80"
                    step="5"
                    value={selfConsumptionRate}
                    onChange={(e) => { setSelfConsumptionRate(e.target.value); setShowResults(false); }}
                    className="w-full accent-amber-400"
                  />
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{ color: '#666' }}>0%</span>
                    <span className="font-bold text-lg px-4 py-1 rounded-full" style={{ backgroundColor: '#FFF5E1', color: '#F8BD3D' }}>
                      {selfConsumptionRate}%
                    </span>
                    <span className="text-sm" style={{ color: '#666' }}>80%</span>
                  </div>
                </div>
                <p className="text-xs mt-1" style={{ color: '#999' }}>{t('simulator.producer.selfConsumptionHint')}</p>
              </div>

              <button
                type="submit"
                className="w-full py-4 rounded-full font-bold text-base transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg hover:-translate-y-0.5"
                style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
              >
                <Zap className="w-5 h-5" />
                {t('simulator.producer.submitButton')}
              </button>
            </form>
          )}

          {showResults && role === 'consumer' && consumerResults && (
            <div className="mt-6 space-y-4 animate-fade-in">
              <div className="h-px" style={{ backgroundColor: '#e5e5e5' }} />
              <h3 className="text-lg font-bold" style={{ color: '#1a1a1a' }}>{t('simulator.consumer.resultsTitle')}</h3>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-2xl text-center" style={{ backgroundColor: '#fafafa', border: '1px solid #e5e5e5' }}>
                  <p className="text-xs font-medium mb-1" style={{ color: '#999' }}>{t('simulator.consumer.currentCost')}</p>
                  <p className="text-2xl font-bold" style={{ color: '#1a1a1a' }}>{consumerResults.currentCost} €</p>
                </div>
                <div className="p-4 rounded-2xl text-center" style={{ backgroundColor: '#FFF5E1', border: '2px solid #F8BD3D' }}>
                  <p className="text-xs font-medium mb-1" style={{ color: '#F8BD3D' }}>{t('simulator.consumer.newCost')}</p>
                  <p className="text-2xl font-bold" style={{ color: '#1a1a1a' }}>{consumerResults.newCost} €</p>
                </div>
              </div>

              <div className="p-5 rounded-2xl text-center" style={{ backgroundColor: '#15A759' }}>
                <p className="text-sm font-semibold mb-1 text-white/80">{t('simulator.consumer.annualSavings')}</p>
                <p className="text-4xl font-black text-white">{consumerResults.savings} €</p>
                <p className="text-sm text-white/80 mt-1">
                  {t('simulator.consumer.savingsDetail', {
                    monthly: consumerResults.monthlySavings,
                    percent: consumerResults.percentageSaved
                  })}
                </p>
              </div>

              <div className="space-y-2">
                {(t('simulator.consumer.benefits', { returnObjects: true }) as string[]).map((item: string, i: number) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#15A759' }} />
                    <span className="text-sm" style={{ color: '#4a4a4a' }}>{item}</span>
                  </div>
                ))}
              </div>

              <p className="text-xs" style={{ color: '#999' }}>
                {t('simulator.consumer.disclaimer', { percent: consumerResults.percentageSaved })}
              </p>
            </div>
          )}

          {showResults && role === 'producer' && producerResults && (
            <div className="mt-6 space-y-4">
              <div className="h-px" style={{ backgroundColor: '#e5e5e5' }} />
              <h3 className="text-lg font-bold" style={{ color: '#1a1a1a' }}>{t('simulator.producer.resultsTitle')}</h3>

              <div className="p-4 rounded-2xl" style={{ backgroundColor: '#fafafa', border: '1px solid #e5e5e5' }}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm" style={{ color: '#666' }}>{t('simulator.producer.annualProduction')}</span>
                  <span className="font-bold" style={{ color: '#1a1a1a' }}>{producerResults.annualProduction.toLocaleString('fr')} kWh</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm" style={{ color: '#666' }}>{t('simulator.producer.surplus')}</span>
                  <span className="font-bold" style={{ color: '#1a1a1a' }}>{producerResults.surplus.toLocaleString('fr')} kWh</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-2xl text-center" style={{ backgroundColor: '#fafafa', border: '1px solid #e5e5e5' }}>
                  <p className="text-xs font-medium mb-1" style={{ color: '#999' }}>{t('simulator.producer.classicInjection')}</p>
                  <p className="text-xs mb-2" style={{ color: '#999' }}>~0.02 €/kWh</p>
                  <p className="text-2xl font-bold" style={{ color: '#1a1a1a' }}>{producerResults.classicRevenue} €</p>
                  <p className="text-xs mt-1" style={{ color: '#999' }}>/ an</p>
                </div>
                <div className="p-4 rounded-2xl text-center" style={{ backgroundColor: '#FFF5E1', border: '2px solid #F8BD3D' }}>
                  <p className="text-xs font-medium mb-1" style={{ color: '#F8BD3D' }}>{t('simulator.producer.withSunIsUp')}</p>
                  <p className="text-xs mb-2" style={{ color: '#F8BD3D' }}>~0.065 €/kWh</p>
                  <p className="text-2xl font-bold" style={{ color: '#1a1a1a' }}>{producerResults.sunIsUpRevenue} €</p>
                  <p className="text-xs mt-1" style={{ color: '#999' }}>/ an</p>
                </div>
              </div>

              <div className="p-5 rounded-2xl text-center" style={{ backgroundColor: '#F8BD3D' }}>
                <p className="text-sm font-semibold mb-1" style={{ color: 'rgba(26,26,26,0.7)' }}>{t('simulator.producer.extraRevenue')}</p>
                <p className="text-4xl font-black" style={{ color: '#1a1a1a' }}>+ {producerResults.extraRevenue} €</p>
                <p className="text-sm mt-1" style={{ color: 'rgba(26,26,26,0.7)' }}>
                  {t('simulator.producer.extraRevenueDetail', { percent: producerResults.extraRevenuePercent })}
                </p>
              </div>

              <div className="space-y-2">
                {(t('simulator.producer.benefits', { returnObjects: true }) as string[]).map((item: string, i: number) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#F8BD3D' }} />
                    <span className="text-sm" style={{ color: '#4a4a4a' }}>{item}</span>
                  </div>
                ))}
              </div>

              <p className="text-xs" style={{ color: '#999' }}>
                {t('simulator.producer.disclaimer')}
              </p>
            </div>
          )}

          {showResults && (
            <button
              onClick={onClose}
              className="mt-4 w-full py-4 rounded-full font-bold text-base transition-all duration-300 flex items-center justify-center gap-2 border-2 hover:-translate-y-0.5"
              style={{ borderColor: '#F8BD3D', color: '#1a1a1a', backgroundColor: 'white' }}
            >
              {t('simulator.contactButton')}
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
